import { e as error, j as json } from "../../../../chunks/index2.js";
import crypto, { randomUUID } from "crypto";
import { c as sendEmailVerification } from "../../../../chunks/mailer.js";
import { h as handleRequest } from "../../../../chunks/utils.js";
import { g as get, V as VerificationCodeCollection, U as UserCollection } from "../../../../chunks/firebase.js";
import { promises } from "dns";
import { getFirestore } from "firebase-admin/firestore";
import { u as updateHealthStatus } from "../../../../chunks/health.js";
const EMAIL_REGEX = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
const DISPOSABLE_DOMAINS = [
  "guerrillamail.com",
  "10minutemail.com",
  "mailinator.com",
  "tempmail.com",
  "throwaway.email",
  "temp-mail.org",
  "guerrillamailblock.com",
  "sharklasers.com",
  "yopmail.com"
  // Add more from mailchecker package or maintain list
];
function validateEmail(email) {
  return EMAIL_REGEX.test(email);
}
async function checkMxRecords(domain) {
  try {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 2e3);
    const records = await promises.resolveMx(domain);
    clearTimeout(timeout);
    return records && records.length > 0;
  } catch (err) {
    return false;
  }
}
function isDisposableEmail(domain) {
  return DISPOSABLE_DOMAINS.includes(domain.toLowerCase());
}
async function validateEmailFull(email) {
  if (!validateEmail(email)) {
    return { valid: false, errorType: "invalid_format" };
  }
  const domain = email.split("@")[1];
  if (isDisposableEmail(domain)) {
    return { valid: false, errorType: "disposable_email" };
  }
  const hasMx = await checkMxRecords(domain);
  if (!hasMx) {
    return { valid: false, errorType: "invalid_domain" };
  }
  return { valid: true };
}
const MAX_TOKENS = 3;
const REFILL_INTERVAL_MS = 1e5;
async function checkRateLimit(email) {
  const db = getFirestore();
  const docRef = db.collection("rate-limits").doc(`email:${email}`);
  const doc = await docRef.get();
  if (!doc.exists) {
    await docRef.set({
      tokens: MAX_TOKENS - 1,
      lastRefill: Date.now(),
      windowMs: 3e5
    });
    return { allowed: true, remainingTokens: MAX_TOKENS - 1 };
  }
  const data = doc.data();
  const now = Date.now();
  const elapsed = now - data.lastRefill;
  const tokensToAdd = Math.floor(elapsed / REFILL_INTERVAL_MS);
  let tokens = Math.min(data.tokens + tokensToAdd, MAX_TOKENS);
  if (tokens > 0) {
    await docRef.update({
      tokens: tokens - 1,
      lastRefill: data.lastRefill + tokensToAdd * REFILL_INTERVAL_MS
    });
    return { allowed: true, remainingTokens: tokens - 1 };
  } else {
    const retryAfterMs = REFILL_INTERVAL_MS - elapsed % REFILL_INTERVAL_MS;
    return { allowed: false, retryAfterMs };
  }
}
var EmailErrorType = /* @__PURE__ */ ((EmailErrorType2) => {
  EmailErrorType2["INVALID_FORMAT"] = "invalid_format";
  EmailErrorType2["INVALID_DOMAIN"] = "invalid_domain";
  EmailErrorType2["DISPOSABLE_EMAIL"] = "disposable_email";
  EmailErrorType2["RATE_LIMITED"] = "rate_limited";
  EmailErrorType2["SERVICE_UNAVAILABLE"] = "service_unavailable";
  EmailErrorType2["NETWORK_TIMEOUT"] = "network_timeout";
  EmailErrorType2["UNKNOWN"] = "unknown";
  return EmailErrorType2;
})(EmailErrorType || {});
function mapPostmarkError(error2) {
  const timestamp = Date.now();
  if (error2.name === "AbortError" || error2.message?.includes("timeout")) {
    return {
      success: false,
      errorType: EmailErrorType.NETWORK_TIMEOUT,
      errorMessage: "Request timed out. Please check your internet connection and try again.",
      timestamp,
      retryEligible: true
    };
  }
  const statusCode = error2.statusCode || error2.status;
  switch (statusCode) {
    case 400:
      return {
        success: false,
        errorType: EmailErrorType.INVALID_FORMAT,
        errorMessage: "Invalid email address format",
        timestamp,
        retryEligible: false
      };
    case 422:
      if (error2.message?.toLowerCase().includes("domain")) {
        return {
          success: false,
          errorType: EmailErrorType.INVALID_DOMAIN,
          errorMessage: "Email domain does not exist or has no mail server",
          timestamp,
          retryEligible: false
        };
      }
      return {
        success: false,
        errorType: EmailErrorType.DISPOSABLE_EMAIL,
        errorMessage: "Disposable email addresses are not allowed",
        timestamp,
        retryEligible: false
      };
    case 429:
      return {
        success: false,
        errorType: EmailErrorType.RATE_LIMITED,
        errorMessage: "Too many verification requests. Please wait before trying again.",
        timestamp,
        retryEligible: true,
        retryAfterMs: 12e4
        // 2 minutes default
      };
    case 500:
    case 503:
      return {
        success: false,
        errorType: EmailErrorType.SERVICE_UNAVAILABLE,
        errorMessage: "Email service is temporarily unavailable. Please try again later.",
        timestamp,
        retryEligible: true
      };
    default:
      return {
        success: false,
        errorType: EmailErrorType.UNKNOWN,
        errorMessage: error2.message || "An unexpected error occurred. Please try again.",
        timestamp,
        retryEligible: true
      };
  }
}
async function withTimeout(promise, timeoutMs, onTimeout) {
  const controller = new AbortController();
  const timeout = setTimeout(() => {
    controller.abort();
  }, timeoutMs);
  try {
    const result = await promise;
    clearTimeout(timeout);
    return result;
  } catch (err) {
    clearTimeout(timeout);
    if (err.name === "AbortError") {
      throw new Error("Request timeout");
    }
    throw err;
  }
}
function hashEmail(email) {
  return crypto.createHash("sha256").update(email).digest("hex").substring(0, 16);
}
function logEmailVerificationAttempt(data) {
  console.log(
    JSON.stringify({
      event: "email_verification_attempt",
      emailHash: hashEmail(data.email),
      success: data.success,
      errorType: data.errorType,
      duration: data.duration,
      timestamp: Date.now()
    })
  );
}
function logCodeVerificationAttempt(data) {
  console.log(
    JSON.stringify({
      event: "code_verification_attempt",
      emailHash: hashEmail(data.email),
      success: data.success,
      attempts: data.attempts,
      timestamp: Date.now()
    })
  );
}
function logRateLimitEvent(data) {
  console.log(
    JSON.stringify({
      event: "rate_limit_check",
      emailHash: hashEmail(data.email),
      allowed: data.allowed,
      remainingTokens: data.remainingTokens,
      retryAfterMs: data.retryAfterMs,
      timestamp: Date.now()
    })
  );
}
const GET = handleRequest(async ({ url, locals }) => {
  const { searchParams } = url;
  const email = searchParams.get("email");
  const code = searchParams.get("code");
  if (email && code) {
    const verificationCode = await get(VerificationCodeCollection(), email);
    if (verificationCode.data) {
      const isExpired = verificationCode.data.expires < Date.now();
      if (isExpired) {
        await verificationCode.ref.delete();
        return error(410, {
          message: "Verification code expired",
          errorType: "code_expired"
        });
      }
      if (verificationCode.data.attempts >= 5) {
        await verificationCode.ref.delete();
        return error(403, {
          message: "Too many failed attempts. Please request a new verification code."
        });
      }
      const verified = verificationCode.data.code === code;
      if (verified) {
        logCodeVerificationAttempt({
          email,
          success: true,
          attempts: verificationCode.data.attempts
        });
        await verificationCode.ref.delete();
        await UserCollection().doc(locals.user?.id).set({ email }, { merge: true });
        return json({ verified: true }, { status: 200 });
      } else {
        const newAttempts = verificationCode.data.attempts + 1;
        logCodeVerificationAttempt({
          email,
          success: false,
          attempts: newAttempts
        });
        await verificationCode.ref.update({
          attempts: newAttempts
        });
        return json({ verified: false }, { status: 403 });
      }
    }
    return error(404, {
      message: "Verification code not found"
    });
  }
  if (email) {
    const validation = await validateEmailFull(email);
    if (!validation.valid) {
      const statusCode = validation.errorType === "invalid_format" ? 400 : 422;
      return error(statusCode, {
        errorType: validation.errorType,
        message: validation.errorType === "invalid_format" ? "Invalid email address format" : validation.errorType === "disposable_email" ? "Disposable email addresses are not allowed" : "Email domain does not exist or has no mail server",
        retryEligible: false
      });
    }
    const rateLimit = await checkRateLimit(email);
    logRateLimitEvent({
      email,
      allowed: rateLimit.allowed,
      remainingTokens: rateLimit.remainingTokens,
      retryAfterMs: rateLimit.retryAfterMs
    });
    if (!rateLimit.allowed) {
      return error(429, {
        errorType: EmailErrorType.RATE_LIMITED,
        message: "Too many verification requests. Please wait before trying again.",
        retryEligible: true,
        retryAfterMs: rateLimit.retryAfterMs
      });
    }
    const id = randomUUID().split("-").join("");
    const verificationCode = id.substring(0, 6).toUpperCase();
    try {
      const startTime2 = Date.now();
      await withTimeout(sendEmailVerification(email, verificationCode), 1e4);
      const duration = Date.now() - startTime2;
      await VerificationCodeCollection().doc(email).set({
        code: verificationCode,
        email,
        expires: Date.now() + 1e3 * 60 * 5,
        // 5 minutes
        sentAt: Date.now(),
        attempts: 0,
        createdAt: Date.now()
      });
      await updateHealthStatus({
        success: true,
        timestamp: Date.now(),
        retryEligible: false,
        metadata: { duration }
      });
      logEmailVerificationAttempt({
        email,
        success: true,
        duration
      });
      return json({ success: true });
    } catch (err) {
      const emailResult = mapPostmarkError(err);
      await updateHealthStatus(emailResult);
      logEmailVerificationAttempt({
        email,
        success: false,
        errorType: emailResult.errorType,
        duration: Date.now() - startTime
      });
      const statusCode = emailResult.errorType === EmailErrorType.RATE_LIMITED ? 429 : emailResult.errorType === EmailErrorType.SERVICE_UNAVAILABLE ? 503 : 500;
      return error(statusCode, {
        errorType: emailResult.errorType,
        message: emailResult.errorMessage,
        retryEligible: emailResult.retryEligible,
        retryAfterMs: emailResult.retryAfterMs
      });
    }
  }
  return error(400, {
    message: "No email provided"
  });
});
export {
  GET
};
